import type { DashboardTextData, DashboardTextLayout } from '@pm-next/shared-types';
import { humanizeLabel } from '@pm-next/shared-types';

interface TextBlockProps {
  data: DashboardTextData;
  layout?: DashboardTextLayout | null;
  /**
   * When false, skip the headline (dashboard cards already show the block title).
   * Body/summary and metrics still render.
   */
  showHeadline?: boolean;
  /** Optional block title used to suppress a duplicate headline string. */
  blockTitle?: string;
}

export function TextBlock({ data, layout, showHeadline = true, blockTitle }: TextBlockProps) {
  const resolvedLayout = layout ?? data.layout ?? 'cards';
  const metrics = data.metrics ?? [];
  const headline =
    data.headline &&
    (!blockTitle || data.headline.trim().toLowerCase() !== blockTitle.trim().toLowerCase())
      ? data.headline
      : null;
  const shouldShowHeadline = showHeadline && !!headline;

  return (
    <div className="flex h-full flex-col gap-3 overflow-auto p-1">
      {(shouldShowHeadline || data.body) && (
        <div>
          {shouldShowHeadline && (
            <p className="text-2xl font-semibold tracking-tight">{headline}</p>
          )}
          {data.body && (
            <p
              className={
                shouldShowHeadline
                  ? 'mt-1 text-sm text-muted-foreground'
                  : 'text-sm text-muted-foreground'
              }
            >
              {data.body}
            </p>
          )}
        </div>
      )}

      {metrics.length > 0 && resolvedLayout === 'list' && (
        <div className="min-h-0 flex-1 overflow-auto">
          <table className="w-full text-left text-sm">
            <thead className="sticky top-0 bg-card">
              <tr className="border-b text-muted-foreground">
                <th className="px-2 py-1.5 font-medium">Key</th>
                <th className="px-2 py-1.5 font-medium">Value</th>
              </tr>
            </thead>
            <tbody>
              {metrics.map((m) => (
                <tr key={m.label} className="border-b border-border/60">
                  <td className="px-2 py-1.5 align-top text-muted-foreground">
                    {humanizeLabel(m.label)}
                  </td>
                  <td className="px-2 py-1.5 align-top font-medium">
                    {m.formattedValue ?? m.value}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {metrics.length > 0 && resolvedLayout !== 'list' && (
        <dl className="grid grid-cols-2 gap-2">
          {metrics.map((m) => (
            <div key={m.label} className="rounded-md bg-muted/50 px-2 py-1.5">
              <dt className="text-[11px] uppercase tracking-wide text-muted-foreground">
                {humanizeLabel(m.label)}
              </dt>
              <dd className="text-sm font-medium">{m.formattedValue ?? m.value}</dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  );
}
